import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ClientForm } from "@/components/invoice/ClientForm";
import { PageHeader } from "@/components/layout/PageHeader";
import { ErrorPanel, LoadingPanel } from "@/components/ui/Loading";
import { useToast } from "@/components/ui/Toast";
import type { BootstrapPayload, ClientProfile } from "@/domain/types";
import { clientDefaults, formToClientInput, type ClientFormValues } from "@/features/invoices/client-form";
import { apiRequest } from "@/lib/api";

export function ClientEditorPage() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [bootstrap, setBootstrap] = useState<BootstrapPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiRequest<BootstrapPayload>("/api/admin/bootstrap"),
      id ? apiRequest<ClientProfile>(`/api/admin/clients/${id}`) : Promise.resolve(null),
    ])
      .then(([nextBootstrap, nextProfile]) => {
        if (cancelled) return;
        setBootstrap(nextBootstrap);
        setProfile(nextProfile);
      })
      .catch((caught) => {
        if (cancelled) return;
        setError(caught instanceof Error ? caught : new Error("Could not load client."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const submit = async (values: ClientFormValues) => {
    try {
      const input = formToClientInput(values);
      const saved = await apiRequest<ClientProfile>(
        editing ? `/api/admin/clients/${id}` : "/api/admin/clients",
        { method: editing ? "PUT" : "POST", body: input },
      );
      showToast(editing ? "Client profile updated." : "Client profile created.");
      navigate(`/app/clients/${saved.id}/edit`, { replace: true });
      setProfile(saved);
    } catch (caught) {
      showToast(caught instanceof Error ? caught.message : "Could not save client.", "error");
    }
  };

  if (loading) return <LoadingPanel label="Loading client…" />;
  if (error) return <ErrorPanel error={error} />;
  if (!bootstrap) return <ErrorPanel error={new Error("Workspace settings could not be loaded.")} />;

  return (
    <>
      <PageHeader
        eyebrow="Client profiles"
        title={editing ? `Edit ${profile?.name ?? "client"}` : "Create client"}
        description="Set reusable defaults for this client. Every individual invoice can override them."
      />
      <ClientForm
        initialValues={clientDefaults(profile ?? undefined, bootstrap.workspaceSettings.defaultCurrency)}
        submitLabel={editing ? "Save client" : "Create client"}
        onSubmit={submit}
      />
    </>
  );
}
